import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { getCenterScopedUrl, matchesCurrentContext, shouldUseCenterScopedApi } from "../../utils/centerScope";
import { getAssignedCenterId } from "../../utils/userSession";
import useDataStore from "../../store/dataStore";

const BASE_URL = "https://mis.empowerplus.in";

export default function EnrollmentVerificationPage() {

    const token = localStorage.getItem("token");
    const assignedCenterId = getAssignedCenterId();
    const store = useDataStore();
    const CACHE_KEY = "enrollmentVerifications";

    /* ================= COMMON ================= */

    const [msg, setMsg] = useState({ type: "", text: "" });

    const flash = (type, text) => {
        setMsg({ type, text });
    };

    const authHeaders = useMemo(() => ({
        Authorization: `Bearer ${token}`
    }), [token]);

    const safeJson = async (res) => {
        const t = await res.text();
        try {
            return t ? JSON.parse(t) : [];
        } catch {
            return [];
        }
    };

    /* ================= EXCEL ================= */

    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [exporting, setExporting] = useState(false);

    const CHUNK_SIZE = 500;
    const PARALLEL = 2;

    const uploadExcel = async () => {
        if (!file) return flash("err", "Select Excel file");

        let dataRows;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            // Send ALL rows including header (row 0) — backend uses header names to find columns
            dataRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }

        if (dataRows.length < 2) return flash("err", "Excel file is empty or has no data rows");

        const headerRow = dataRows[0];
        const dataOnly = dataRows.slice(1);
        const totalRows = dataOnly.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);

        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });

        let totalInserted = 0, totalSkipped = 0, failed = false, firstError = "";

        const uploadOneChunk = async (i) => {
            // Prepend header row to every chunk so backend can map columns
            const chunkRows = [headerRow, ...dataOnly.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)];
            const res = await fetch(
                `${BASE_URL}/api/enrollment-verification/upload-json?actor=Rahul`,
                {
                    method: "POST",
                    headers: { ...authHeaders, "Content-Type": "application/json" },
                    body: JSON.stringify(chunkRows),
                }
            );
            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
            const data = await res.json();
            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);

            const ins = data?.inserted ?? 0;
            const skp = data?.skipped ?? 0;
            const skippedRows = data?.skippedRows ?? [];

            console.log(`[Chunk ${i + 1}/${totalChunks}] Inserted: ${ins} | Skipped: ${skp}`);
            if (skippedRows.length > 0) {
                console.group(`[Chunk ${i + 1}] Skipped rows (${skippedRows.length}):`);
                skippedRows.forEach(r => console.log(r));
                console.groupEnd();
            }
            return { inserted: ins, skipped: skp };
        };

        let uploadedSoFar = 0;
        for (let start = 0; start < totalChunks && !failed; start += PARALLEL) {
            const indices = Array.from({ length: Math.min(PARALLEL, totalChunks - start) }, (_, k) => start + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: start + indices.length, totalChunks });

            const results = await Promise.allSettled(indices.map(uploadOneChunk));
            for (const r of results) {
                if (r.status === "fulfilled") {
                    totalInserted += r.value.inserted;
                    totalSkipped += r.value.skipped;
                    uploadedSoFar += r.value.inserted + r.value.skipped;
                } else {
                    failed = true;
                    firstError = r.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: start + indices.length, totalChunks, error: failed });
        }

        setUploading(false);
        if (!failed) {
            console.log(`[Upload Summary] Inserted: ${totalInserted} | Skipped: ${totalSkipped} | Total: ${totalRows}`);
            flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()}`);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchAllEnrollments();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    /* ================= FETCH ================= */

    const [rows, setRows] = useState(() => store.getRows(CACHE_KEY));
    const [loading, setLoading] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(500);
    const [totalCount, setTotalCount] = useState(null);

    const fetchFirst100 = async () => {
        if (store.isCached(CACHE_KEY)) {
            setRows(store.getRows(CACHE_KEY));
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(
                getCenterScopedUrl(BASE_URL, "/api/enrollment-verification/page?page=0&size=100"),
                { headers: authHeaders }
            );
            const data = await safeJson(res);
            const pageRows = data?.content ?? (Array.isArray(data) ? data : []);
            const scopedRows = shouldUseCenterScopedApi()
                ? pageRows.filter(matchesCurrentContext)
                : pageRows;
            setRows(scopedRows);
            setAllLoaded(false);
            store.setRows(CACHE_KEY, scopedRows, false);
        } catch {
            setRows([]);
        }
        setLoading(false);
    };

    const fetchAllEnrollments = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(
                getCenterScopedUrl(BASE_URL, "/api/enrollment-verification"),
                { headers: authHeaders }
            );
            const nextRows = await safeJson(res);
            const scopedRows = shouldUseCenterScopedApi()
                ? nextRows.filter(matchesCurrentContext)
                : nextRows;
            setRows(scopedRows);
            setVisibleCount(scopedRows.length);
            setAllLoaded(true);
            store.setRows(CACHE_KEY, scopedRows, true);
        } catch {
            setRows([]);
        }
        setLoadingAll(false);
    };

    useEffect(() => {
        fetchFirst100();
    }, []);

    useEffect(() => {
        fetch(`${BASE_URL}/api/dashboard/counts`, { headers: authHeaders })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d) setTotalCount(d.enrollmentVerifications ?? null); })
            .catch(() => {});
    }, []);

    /* ================= SEARCH ================= */

    useEffect(() => { setVisibleCount(500); }, [search]);

    const filtered = useMemo(() => {

        const q = search.toLowerCase();

        return rows.filter(r =>
            r.mobileNo?.toLowerCase().includes(q) ||
            r.name?.toLowerCase().includes(q) ||
            r.projectName?.toLowerCase().includes(q)
        );

    }, [rows, search]);

    /* ================= DELETE ================= */

    const deleteEnrollment = async (id) => {

        if (!window.confirm("Delete this record?")) return;

        await fetch(`${BASE_URL}/api/enrollment-verification/${id}`, {
            method: "DELETE",
            headers: authHeaders
        });

        flash("ok", "Record deleted");

        store.invalidate(CACHE_KEY);
        fetchAllEnrollments();

    };

    /* ================= AUDIT ================= */

    const [auditOpen, setAuditOpen] = useState(false);
    const [auditRows, setAuditRows] = useState([]);

    const openAuditAll = async () => {

        setAuditOpen(true);

        const res = await fetch(`${BASE_URL}/api/enrollment-verification/audits`, {
            headers: authHeaders
        });

        setAuditRows(await safeJson(res));

    };

    const exportExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (shouldUseCenterScopedApi() && assignedCenterId) {
                params.set("centerId", assignedCenterId);
            }
            const url = `${BASE_URL}/api/enrollment-verification/export${params.toString() ? "?" + params.toString() : ""}`;
            const res = await fetch(url, { headers: authHeaders });
            if (!res.ok) throw new Error(`Export failed (${res.status})`);
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "enrollment_verification.xlsx";
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(a.href);
        } catch (err) {
            flash("err", err.message || "Export failed");
        } finally {
            setExporting(false);
        }
    };

    /* ================= TEMPLATE ================= */

    const downloadTemplate = () => {
        const a = document.createElement("a");
        a.href = "/templates/enrollment_verification_upload_template.xlsx";
        a.download = "enrollment_verification_upload_template.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    /* ================= UI ================= */

    return (

        <div className="cmp-page">

            <div className="header">
                <h1>Enrollment Verification</h1>

                {/* <button className="btn" onClick={openAuditAll}>
                    View Audit Logs
                </button> */}

            </div>

            {msg.text && (
                <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            )}

            {/* Upload */}

            <div className="card grid-3">

                <input
                    type="file"
                    onChange={e => setFile(e.target.files[0])}
                />

                <button className="btn btn-primary" onClick={uploadExcel}>
                    {uploading ? "Uploading..." : "Upload Excel"}
                </button>

                <button className="btn btn-outline" onClick={downloadTemplate}>
                    Download Excel Template
                </button>

            </div>

            {uploadProgress && (
                <div className="card" style={{ padding: "10px 16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
                        <span>
                            {uploadProgress.error
                                ? "Upload failed"
                                : uploadProgress.chunk >= uploadProgress.totalChunks
                                    ? "Upload complete"
                                    : `Uploading chunk ${uploadProgress.chunk} / ${uploadProgress.totalChunks}…`}
                        </span>
                        <span>{uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows</span>
                    </div>
                    <div style={{ background: "#e5e7eb", borderRadius: 8, height: 8, overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            borderRadius: 8,
                            background: uploadProgress.error ? "#ef4444" : "#2563eb",
                            width: `${uploadProgress.total ? Math.round((uploadProgress.uploaded / uploadProgress.total) * 100) : 0}%`,
                            transition: "width 0.3s"
                        }} />
                    </div>
                </div>
            )}

            {/* Table */}

            <div className="card">

                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input
                        className="search"
                        placeholder="Search mobile / name / project..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ flex: "0 0 55%", marginBottom: 0 }}
                    />
                    {!allLoaded && (
                        <button
                            className="btn btn-outline"
                            onClick={fetchAllEnrollments}
                            disabled={loadingAll}
                            style={{ flex: "0 0 calc(22% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
                        >
                            {loadingAll ? "Loading…" : `Load All Data (${rows.length} loaded)`}
                        </button>
                    )}
                    <button
                        className="btn btn-outline"
                        onClick={exportExcel}
                        disabled={exporting}
                        style={{ flex: "0 0 calc(23% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
                    >
                        {exporting ? "Processing..." : "Export All Data"}
                    </button>
                </div>
                {totalCount !== null && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginTop: 4, marginBottom: 8 }}>
                        Total: {totalCount.toLocaleString()}
                    </div>
                )}

                <div className="table-wrap">

                    <table className="ep-table">

                        <thead>

                            <tr>
                                <th>#</th>
                                {/* <th>ID</th> */}
                                <th>Beneficiary ID</th>
                                <th>Project ID</th>
                                <th>Date</th>
                                <th>Centre</th>
                                <th>Salutation</th>
                                <th>Name</th>
                                <th>Age</th>
                                <th>Gender</th>
                                <th>Mobile</th>
                                <th>WhatsApp No</th>
                                <th>Aadhar No</th>
                                <th>Email</th>
                                <th>Education</th>
                                <th>Guardian</th>
                                <th>Work Experience</th>
                                <th>Willing To Take Job</th>
                                <th>Willing To Migrate</th>
                                <th>Enrolled Last 6 Months</th>
                                <th>Interested Course</th>
                                <th>Heard About Us</th>
                                <th>Counselled</th>
                                <th>ROC Status</th>
                                <th className="actions">Actions</th>
                            </tr>

                        </thead>

                        <tbody>

                            {filtered.slice(0, visibleCount).map((r, i) => (

                                <tr key={r.id}>

                                    <td>{i + 1}</td>
                                    {/* <td>{r.id}</td> */}
                                    <td>{r.beneficiaryId}</td>
                                    <td>{r.projectId}</td>
                                    <td>{r.mobilizationDate}</td>
                                    <td>{r.centreName}</td>
                                    <td>{r.salutation}</td>
                                    <td>{r.name}</td>
                                    <td>{r.age}</td>
                                    <td>{r.gender}</td>
                                    <td>{r.mobileNo}</td>
                                    <td>{r.whatsappNumber}</td>
                                    <td>{r.aadharNumber}</td>
                                    <td>{r.emailId}</td>
                                    <td>{r.highestEducation}</td>
                                    <td>{r.guardianName}</td>
                                    <td>{r.workExperience}</td>
                                    <td>{r.willingToTakeJob}</td>
                                    <td>{r.willingToMigrate}</td>
                                    <td>{r.enrolledLast6Months}</td>
                                    <td>{r.interestedCourseName}</td>
                                    <td>{r.hearAboutUs}</td>
                                    <td>{r.counselled}</td>
                                    <td>{r.rocVerificationStatus}</td>

                                    <td className="actions">

                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => deleteEnrollment(r.id)}
                                        >
                                            Delete
                                        </button>

                                    </td>

                                </tr>

                            ))}

                            {!filtered.length && (
                                <tr>
                                    <td colSpan="25" className="muted">
                                        {loading ? "Loading..." : "No records found"}
                                    </td>
                                </tr>
                            )}
                            {!allLoaded && filtered.length > 0 && (
                                <tr>
                                    <td colSpan="25" style={{ textAlign: "center", padding: "14px" }}>
                                        <button
                                            className="btn btn-outline"
                                            onClick={fetchAllEnrollments}
                                            disabled={loadingAll}
                                        >
                                            {loadingAll ? "Loading all data…" : "Load All Data"}
                                        </button>
                                    </td>
                                </tr>
                            )}
                            {allLoaded && filtered.length > visibleCount && (
                                <tr>
                                    <td colSpan="25" style={{ textAlign: "center", padding: "14px" }}>
                                        <button
                                            className="btn btn-outline"
                                            onClick={() => setVisibleCount((c) => c + 500)}
                                        >
                                            Show More ({filtered.length - visibleCount} remaining)
                                        </button>
                                    </td>
                                </tr>
                            )}

                        </tbody>

                    </table>

                </div>

            </div>

            {/* AUDIT MODAL */}

            {auditOpen && createPortal(

                <div className="modal-overlay" onClick={() => setAuditOpen(false)}>

                    <div className="ep-modal wide" onClick={e => e.stopPropagation()}>

                        <h2>Enrollment Verification Audit</h2>

                        <pre>{JSON.stringify(auditRows, null, 2)}</pre>

                    </div>

                </div>,

                document.body

            )}

            <style>{`

.cmp-page{
padding:22px;
background:#f7f7fb;
min-height:100vh;
font-family:Inter
}

.header{
display:flex;
justify-content:space-between;
align-items:center
}

h1{
font-size:26px;
font-weight:900
}

.flash{
padding:10px;
border-radius:10px;
margin:10px 0;
font-weight:700
}

.flash-ok{
background:#ecfdf5;
color:#065f46
}

.flash-err{
background:#fef2f2;
color:#991b1b
}

.card{
background:#fff;
border:1px solid #e5e7eb;
border-radius:14px;
padding:16px;
margin-bottom:14px
}

.grid-3{
display:grid;
grid-template-columns:1fr auto auto;
gap:10px
}

.btn{
border:1px solid #e5e7eb;
padding:10px 14px;
border-radius:12px;
font-weight:800;
cursor:pointer;
background:#fff
}

.btn-primary{
background:#2563eb;
color:#fff
}

.btn-sm{
padding:6px 10px;
font-size:12px
}

.btn-danger{
color:#991b1b;
border-color:#fca5a5
}

.search{
margin-bottom:10px;
padding:10px;
border-radius:12px;
border:1px solid #e5e7eb
}

.table-wrap{
overflow-x:auto
}

.ep-table{
width:100%;
min-width:1200px;
border-collapse:collapse
}

th,td{
padding:10px;
border-bottom:1px solid #e5e7eb;
font-size:13px;
white-space:nowrap
}

th{
background:#fcfcff;
text-align:left
}

td.actions{
position:sticky;
right:0;
background:#fff
}

.muted{
color:#6b7280;
font-weight:700
}

.modal-overlay{
position:fixed;
inset:0;
background:rgba(0,0,0,.45);
display:flex;
align-items:center;
justify-content:center;
z-index:99999
}

.ep-modal{
background:#fff;
padding:18px;
border-radius:16px;
width:900px;
max-height:90vh;
overflow:auto
}

`}</style>

        </div>

    );

}


// import React, { useEffect, useMemo, useState } from "react";
// import { createPortal } from "react-dom";
// import * as XLSX from "xlsx";
// import { getCenterScopedUrl, matchesCurrentContext, shouldUseCenterScopedApi } from "../../utils/centerScope";

// const BASE_URL = "https://mis.empowerplus.in";

// export default function EnrollmentVerificationPage() {

//     const token = localStorage.getItem("token");

//     /* ================= COMMON ================= */

//     const [msg, setMsg] = useState({ type: "", text: "" });

//     const flash = (type, text) => {
//         setMsg({ type, text });
//     };

//     const authHeaders = useMemo(() => ({
//         Authorization: `Bearer ${token}`
//     }), [token]);

//     const safeJson = async (res) => {
//         const t = await res.text();
//         try {
//             return t ? JSON.parse(t) : [];
//         } catch {
//             return [];
//         }
//     };

//     /* ================= EXCEL ================= */

//     const [file, setFile] = useState(null);
//     const [uploading, setUploading] = useState(false);
//     const [uploadProgress, setUploadProgress] = useState(null);

//     const CHUNK_SIZE = 500;
//     const PARALLEL = 3;

//     const uploadExcel = async () => {
//         if (!file) return flash("err", "Select Excel file");

//         let dataRows;
//         try {
//             const buffer = await file.arrayBuffer();
//             const wb = XLSX.read(buffer, { type: "array" });
//             const ws = wb.Sheets[wb.SheetNames[0]];
//             // Send ALL rows including header (row 0) — backend uses header names to find columns
//             dataRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
//         } catch (err) {
//             return flash("err", `Cannot read file: ${err.message}`);
//         }

//         if (dataRows.length < 2) return flash("err", "Excel file is empty or has no data rows");

//         const headerRow = dataRows[0];
//         const dataOnly = dataRows.slice(1);
//         const totalRows = dataOnly.length;
//         const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);

//         setUploading(true);
//         setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });

//         let totalInserted = 0, totalSkipped = 0, failed = false, firstError = "";

//         const uploadOneChunk = async (i) => {
//             // Prepend header row to every chunk so backend can map columns
//             const chunkRows = [headerRow, ...dataOnly.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)];
//             const res = await fetch(
//                 `${BASE_URL}/api/enrollment-verification/upload-json?actor=Rahul`,
//                 {
//                     method: "POST",
//                     headers: { ...authHeaders, "Content-Type": "application/json" },
//                     body: JSON.stringify(chunkRows),
//                 }
//             );
//             if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
//             const data = await res.json();
//             if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);

//             const ins = data?.inserted ?? 0;
//             const skp = data?.skipped ?? 0;
//             const skippedRows = data?.skippedRows ?? [];

//             console.log(`[Chunk ${i + 1}/${totalChunks}] Inserted: ${ins} | Skipped: ${skp}`);
//             if (skippedRows.length > 0) {
//                 console.group(`[Chunk ${i + 1}] Skipped rows (${skippedRows.length}):`);
//                 skippedRows.forEach(r => console.log(r));
//                 console.groupEnd();
//             }
//             return { inserted: ins, skipped: skp };
//         };

//         let uploadedSoFar = 0;
//         for (let start = 0; start < totalChunks && !failed; start += PARALLEL) {
//             const indices = Array.from({ length: Math.min(PARALLEL, totalChunks - start) }, (_, k) => start + k);
//             setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: start + indices.length, totalChunks });

//             const results = await Promise.allSettled(indices.map(uploadOneChunk));
//             for (const r of results) {
//                 if (r.status === "fulfilled") {
//                     totalInserted += r.value.inserted;
//                     totalSkipped += r.value.skipped;
//                     uploadedSoFar += r.value.inserted + r.value.skipped;
//                 } else {
//                     failed = true;
//                     firstError = r.reason?.message || "Upload failed";
//                 }
//             }
//             setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: start + indices.length, totalChunks, error: failed });
//         }

//         setUploading(false);
//         if (!failed) {
//             console.log(`[Upload Summary] Inserted: ${totalInserted} | Skipped: ${totalSkipped} | Total: ${totalRows}`);
//             flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()}`);
//             setFile(null);
//             setUploadProgress(null);
//             fetchAllEnrollments();
//         } else {
//             flash("err", firstError);
//             setUploadProgress(prev => prev ? { ...prev, error: true } : null);
//         }
//     };

//     /* ================= FETCH ================= */

//     const [rows, setRows] = useState([]);
//     const [loading, setLoading] = useState(false);
//     const [loadingAll, setLoadingAll] = useState(false);
//     const [allLoaded, setAllLoaded] = useState(false);
//     const [search, setSearch] = useState("");
//     const [visibleCount, setVisibleCount] = useState(500);

//     const fetchFirst100 = async () => {
//         setLoading(true);
//         try {
//             const res = await fetch(
//                 getCenterScopedUrl(BASE_URL, "/api/enrollment-verification/page?page=0&size=100"),
//                 { headers: authHeaders }
//             );
//             const data = await safeJson(res);
//             const pageRows = data?.content ?? (Array.isArray(data) ? data : []);
//             const scopedRows = shouldUseCenterScopedApi()
//                 ? pageRows.filter(matchesCurrentContext)
//                 : pageRows;
//             setRows(scopedRows);
//             setAllLoaded(false);
//         } catch {
//             setRows([]);
//         }
//         setLoading(false);
//     };

//     const fetchAllEnrollments = async () => {
//         setLoadingAll(true);
//         try {
//             const res = await fetch(
//                 getCenterScopedUrl(BASE_URL, "/api/enrollment-verification"),
//                 { headers: authHeaders }
//             );
//             const nextRows = await safeJson(res);
//             const scopedRows = shouldUseCenterScopedApi()
//                 ? nextRows.filter(matchesCurrentContext)
//                 : nextRows;
//             setRows(scopedRows);
//             setVisibleCount(scopedRows.length);
//             setAllLoaded(true);
//         } catch {
//             setRows([]);
//         }
//         setLoadingAll(false);
//     };

//     useEffect(() => {
//         fetchFirst100();
//     }, []);

//     /* ================= SEARCH ================= */

//     useEffect(() => { setVisibleCount(500); }, [search]);

//     const filtered = useMemo(() => {

//         const q = search.toLowerCase();

//         return rows.filter(r =>
//             r.mobileNo?.toLowerCase().includes(q) ||
//             r.name?.toLowerCase().includes(q) ||
//             r.projectName?.toLowerCase().includes(q)
//         );

//     }, [rows, search]);

//     /* ================= DELETE ================= */

//     const deleteEnrollment = async (id) => {

//         if (!window.confirm("Delete this record?")) return;

//         await fetch(`${BASE_URL}/api/enrollment-verification/${id}`, {
//             method: "DELETE",
//             headers: authHeaders
//         });

//         flash("ok", "Record deleted");

//         fetchAllEnrollments();

//     };

//     /* ================= AUDIT ================= */

//     const [auditOpen, setAuditOpen] = useState(false);
//     const [auditRows, setAuditRows] = useState([]);

//     const openAuditAll = async () => {

//         setAuditOpen(true);

//         const res = await fetch(`${BASE_URL}/api/enrollment-verification/audits`, {
//             headers: authHeaders
//         });

//         setAuditRows(await safeJson(res));

//     };

//     const exportExcel = async () => {
//         if (!rows.length) return flash("err", "No data to export");
//         let exportRows = rows;
//         if (!allLoaded) {
//             setLoadingAll(true);
//             try {
//                 const res = await fetch(
//                     getCenterScopedUrl(BASE_URL, "/api/enrollment-verification"),
//                     { headers: authHeaders }
//                 );
//                 const nextRows = await safeJson(res);
//                 exportRows = shouldUseCenterScopedApi()
//                     ? nextRows.filter(matchesCurrentContext)
//                     : nextRows;
//                 setRows(exportRows);
//                 setVisibleCount(exportRows.length);
//                 setAllLoaded(true);
//             } catch {
//                 // export with whatever rows we have
//             }
//             setLoadingAll(false);
//         }
//         const ws = XLSX.utils.json_to_sheet(exportRows);
//         const wb = XLSX.utils.book_new();
//         XLSX.utils.book_append_sheet(wb, ws, "Enrollments");
//         XLSX.writeFile(wb, "enrollment_verification.xlsx");
//     };

//     /* ================= TEMPLATE ================= */

//     const downloadTemplate = () => {
//         const a = document.createElement("a");
//         a.href = "/templates/enrollment_verification_upload_template.xlsx";
//         a.download = "enrollment_verification_upload_template.xlsx";
//         document.body.appendChild(a);
//         a.click();
//         a.remove();
//     };

//     /* ================= UI ================= */

//     return (

//         <div className="cmp-page">

//             <div className="header">
//                 <h1>Enrollment Verification</h1>

//                 {/* <button className="btn" onClick={openAuditAll}>
//                     View Audit Logs
//                 </button> */}

//             </div>

//             {msg.text && (
//                 <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
//                     <span>{msg.text}</span>
//                     <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
//                 </div>
//             )}

//             {/* Upload */}

//             <div className="card grid-3">

//                 <input
//                     type="file"
//                     onChange={e => setFile(e.target.files[0])}
//                 />

//                 <button className="btn btn-primary" onClick={uploadExcel}>
//                     {uploading ? "Uploading..." : "Upload Excel"}
//                 </button>

//                 <button className="btn btn-outline" onClick={downloadTemplate}>
//                     Download Excel Template
//                 </button>

//             </div>

//             {uploadProgress && (
//                 <div className="card" style={{ padding: "10px 16px" }}>
//                     <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
//                         <span>
//                             {uploadProgress.error
//                                 ? "Upload failed"
//                                 : uploadProgress.chunk >= uploadProgress.totalChunks
//                                     ? "Upload complete"
//                                     : `Uploading chunk ${uploadProgress.chunk} / ${uploadProgress.totalChunks}…`}
//                         </span>
//                         <span>{uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows</span>
//                     </div>
//                     <div style={{ background: "#e5e7eb", borderRadius: 8, height: 8, overflow: "hidden" }}>
//                         <div style={{
//                             height: "100%",
//                             borderRadius: 8,
//                             background: uploadProgress.error ? "#ef4444" : "#2563eb",
//                             width: `${uploadProgress.total ? Math.round((uploadProgress.uploaded / uploadProgress.total) * 100) : 0}%`,
//                             transition: "width 0.3s"
//                         }} />
//                     </div>
//                 </div>
//             )}

//             {/* Table */}

//             <div className="card">

//                 <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
//                     <input
//                         className="search"
//                         placeholder="Search mobile / name / project..."
//                         value={search}
//                         onChange={e => setSearch(e.target.value)}
//                         style={{ flex: "0 0 55%", marginBottom: 0 }}
//                     />
//                     {!allLoaded && (
//                         <button
//                             className="btn btn-outline"
//                             onClick={fetchAllEnrollments}
//                             disabled={loadingAll}
//                             style={{ flex: "0 0 calc(22% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
//                         >
//                             {loadingAll ? "Loading…" : `Load All Data (${rows.length} loaded)`}
//                         </button>
//                     )}
//                     <button
//                         className="btn btn-outline"
//                         onClick={exportExcel}
//                         style={{ flex: "0 0 calc(23% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
//                     >
//                         Export All Data
//                     </button>
//                 </div>

//                 <div className="table-wrap">

//                     <table className="ep-table">

//                         <thead>

//                             <tr>
//                                 <th>#</th>
//                                 {/* <th>ID</th> */}
//                                 <th>Beneficiary ID</th>
//                                 <th>Project ID</th>
//                                 <th>Date</th>
//                                 <th>Centre</th>
//                                 <th>Salutation</th>
//                                 <th>Name</th>
//                                 <th>Age</th>
//                                 <th>Gender</th>
//                                 <th>Mobile</th>
//                                 <th>WhatsApp No</th>
//                                 <th>Aadhar No</th>
//                                 <th>Email</th>
//                                 <th>Education</th>
//                                 <th>Guardian</th>
//                                 <th>Work Experience</th>
//                                 <th>Willing To Take Job</th>
//                                 <th>Willing To Migrate</th>
//                                 <th>Enrolled Last 6 Months</th>
//                                 <th>Interested Course</th>
//                                 <th>Heard About Us</th>
//                                 <th>Counselled</th>
//                                 <th>ROC Status</th>
//                                 <th className="actions">Actions</th>
//                             </tr>

//                         </thead>

//                         <tbody>

//                             {filtered.slice(0, visibleCount).map((r, i) => (

//                                 <tr key={r.id}>

//                                     <td>{i + 1}</td>
//                                     {/* <td>{r.id}</td> */}
//                                     <td>{r.beneficiaryId}</td>
//                                     <td>{r.projectId}</td>
//                                     <td>{r.mobilizationDate}</td>
//                                     <td>{r.centreName}</td>
//                                     <td>{r.salutation}</td>
//                                     <td>{r.name}</td>
//                                     <td>{r.age}</td>
//                                     <td>{r.gender}</td>
//                                     <td>{r.mobileNo}</td>
//                                     <td>{r.whatsappNumber}</td>
//                                     <td>{r.aadharNumber}</td>
//                                     <td>{r.emailId}</td>
//                                     <td>{r.highestEducation}</td>
//                                     <td>{r.guardianName}</td>
//                                     <td>{r.workExperience}</td>
//                                     <td>{r.willingToTakeJob}</td>
//                                     <td>{r.willingToMigrate}</td>
//                                     <td>{r.enrolledLast6Months}</td>
//                                     <td>{r.interestedCourseName}</td>
//                                     <td>{r.hearAboutUs}</td>
//                                     <td>{r.counselled}</td>
//                                     <td>{r.rocVerificationStatus}</td>

//                                     <td className="actions">

//                                         <button
//                                             className="btn btn-sm btn-danger"
//                                             onClick={() => deleteEnrollment(r.id)}
//                                         >
//                                             Delete
//                                         </button>

//                                     </td>

//                                 </tr>

//                             ))}

//                             {!filtered.length && (
//                                 <tr>
//                                     <td colSpan="25" className="muted">
//                                         {loading ? "Loading..." : "No records found"}
//                                     </td>
//                                 </tr>
//                             )}
//                             {!allLoaded && filtered.length > 0 && (
//                                 <tr>
//                                     <td colSpan="25" style={{ textAlign: "center", padding: "14px" }}>
//                                         <button
//                                             className="btn btn-outline"
//                                             onClick={fetchAllEnrollments}
//                                             disabled={loadingAll}
//                                         >
//                                             {loadingAll ? "Loading all data…" : "Load All Data"}
//                                         </button>
//                                     </td>
//                                 </tr>
//                             )}
//                             {allLoaded && filtered.length > visibleCount && (
//                                 <tr>
//                                     <td colSpan="25" style={{ textAlign: "center", padding: "14px" }}>
//                                         <button
//                                             className="btn btn-outline"
//                                             onClick={() => setVisibleCount((c) => c + 500)}
//                                         >
//                                             Show More ({filtered.length - visibleCount} remaining)
//                                         </button>
//                                     </td>
//                                 </tr>
//                             )}

//                         </tbody>

//                     </table>

//                 </div>

//             </div>

//             {/* AUDIT MODAL */}

//             {auditOpen && createPortal(

//                 <div className="modal-overlay" onClick={() => setAuditOpen(false)}>

//                     <div className="ep-modal wide" onClick={e => e.stopPropagation()}>

//                         <h2>Enrollment Verification Audit</h2>

//                         <pre>{JSON.stringify(auditRows, null, 2)}</pre>

//                     </div>

//                 </div>,

//                 document.body

//             )}

//             <style>{`

// .cmp-page{
// padding:22px;
// background:#f7f7fb;
// min-height:100vh;
// font-family:Inter
// }

// .header{
// display:flex;
// justify-content:space-between;
// align-items:center
// }

// h1{
// font-size:26px;
// font-weight:900
// }

// .flash{
// padding:10px;
// border-radius:10px;
// margin:10px 0;
// font-weight:700
// }

// .flash-ok{
// background:#ecfdf5;
// color:#065f46
// }

// .flash-err{
// background:#fef2f2;
// color:#991b1b
// }

// .card{
// background:#fff;
// border:1px solid #e5e7eb;
// border-radius:14px;
// padding:16px;
// margin-bottom:14px
// }

// .grid-3{
// display:grid;
// grid-template-columns:1fr auto auto;
// gap:10px
// }

// .btn{
// border:1px solid #e5e7eb;
// padding:10px 14px;
// border-radius:12px;
// font-weight:800;
// cursor:pointer;
// background:#fff
// }

// .btn-primary{
// background:#2563eb;
// color:#fff
// }

// .btn-sm{
// padding:6px 10px;
// font-size:12px
// }

// .btn-danger{
// color:#991b1b;
// border-color:#fca5a5
// }

// .search{
// margin-bottom:10px;
// padding:10px;
// border-radius:12px;
// border:1px solid #e5e7eb
// }

// .table-wrap{
// overflow-x:auto
// }

// .ep-table{
// width:100%;
// min-width:1200px;
// border-collapse:collapse
// }

// th,td{
// padding:10px;
// border-bottom:1px solid #e5e7eb;
// font-size:13px;
// white-space:nowrap
// }

// th{
// background:#fcfcff;
// text-align:left
// }

// td.actions{
// position:sticky;
// right:0;
// background:#fff
// }

// .muted{
// color:#6b7280;
// font-weight:700
// }

// .modal-overlay{
// position:fixed;
// inset:0;
// background:rgba(0,0,0,.45);
// display:flex;
// align-items:center;
// justify-content:center;
// z-index:99999
// }

// .ep-modal{
// background:#fff;
// padding:18px;
// border-radius:16px;
// width:900px;
// max-height:90vh;
// overflow:auto
// }

// `}</style>

//         </div>

//     );

// }



// // import React, { useEffect, useMemo, useState } from "react";
// // import { createPortal } from "react-dom";
// // import * as XLSX from "xlsx";
// // import { getCenterScopedUrl, matchesCurrentContext, shouldUseCenterScopedApi } from "../../utils/centerScope";

// // const BASE_URL = "https://mis.empowerplus.in";

// // export default function EnrollmentVerificationPage() {

// //     const token = localStorage.getItem("token");

// //     /* ================= COMMON ================= */

// //     const [msg, setMsg] = useState({ type: "", text: "" });

// //     const flash = (type, text) => {
// //         setMsg({ type, text });
// //     };

// //     const authHeaders = useMemo(() => ({
// //         Authorization: `Bearer ${token}`
// //     }), [token]);

// //     const safeJson = async (res) => {
// //         const t = await res.text();
// //         try {
// //             return t ? JSON.parse(t) : [];
// //         } catch {
// //             return [];
// //         }
// //     };

// //     /* ================= EXCEL ================= */

// //     const [file, setFile] = useState(null);
// //     const [uploading, setUploading] = useState(false);

// //     const uploadExcel = async () => {

// //         if (!file) return flash("err", "Select Excel file");

// //         if (file.size > 40 * 1024 * 1024) {
// //             return flash("err", "File is too large (max 40 MB). Please split the data into smaller files.");
// //         }

// //         const fd = new FormData();
// //         fd.append("file", file);

// //         setUploading(true);

// //         try {

// //             const res = await fetch(`${BASE_URL}/api/enrollment-verification/upload?actor=Rahul`, {
// //                 method: "POST",
// //                 headers: authHeaders,
// //                 body: fd
// //             });

// //             if (res.status === 413) {
// //                 flash("err", "File too large for server (413). Ask your admin to increase nginx client_max_body_size.");
// //                 return;
// //             }

// //             if (!res.ok) {
// //                 const text = await res.text();
// //                 let errMsg = `Upload failed (${res.status})`;
// //                 try { const j = JSON.parse(text); errMsg = j.message || errMsg; } catch {}
// //                 flash("err", errMsg);
// //                 return;
// //             }

// //             const resText = await res.text();
// //             let resData; try { resData = resText ? JSON.parse(resText) : null; } catch { resData = null; }
// //             const cnt = resData?.successCount ?? resData?.inserted ?? resData?.totalRows ?? resData?.count ?? resData?.saved ?? resData?.rowsCreated ?? resData?.rowsInserted ?? null;
// //             flash("ok", `Excel uploaded${cnt != null ? ` — ${cnt} rows` : " successfully"}`);

// //             setFile(null);
// //             fetchEnrollments();

// //         } catch (err) {

// //             flash("err", "Upload failed — check your network connection");

// //         } finally {
// //             setUploading(false);
// //         }

// //     };

// //     /* ================= FETCH ================= */

// //     const [rows, setRows] = useState([]);
// //     const [loading, setLoading] = useState(false);
// //     const [search, setSearch] = useState("");
// //     const [visibleCount, setVisibleCount] = useState(500);

// //     const fetchEnrollments = async () => {

// //         setLoading(true);

// //         try {

// //             const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/enrollment-verification"), {
// //                 headers: authHeaders
// //             });

// //             const nextRows = await safeJson(res);
// //             const scopedRows = shouldUseCenterScopedApi()
// //                 ? nextRows.filter(matchesCurrentContext)
// //                 : nextRows;

// //             setRows(scopedRows);

// //         } catch {
// //             setRows([]);
// //         }

// //         setLoading(false);

// //     };

// //     useEffect(() => {
// //         fetchEnrollments();
// //     }, []);

// //     /* ================= SEARCH ================= */

// //     useEffect(() => { setVisibleCount(500); }, [search]);

// //     const filtered = useMemo(() => {

// //         const q = search.toLowerCase();

// //         return rows.filter(r =>
// //             r.mobileNo?.toLowerCase().includes(q) ||
// //             r.name?.toLowerCase().includes(q) ||
// //             r.projectName?.toLowerCase().includes(q)
// //         );

// //     }, [rows, search]);

// //     /* ================= DELETE ================= */

// //     const deleteEnrollment = async (id) => {

// //         if (!window.confirm("Delete this record?")) return;

// //         await fetch(`${BASE_URL}/api/enrollment-verification/${id}`, {
// //             method: "DELETE",
// //             headers: authHeaders
// //         });

// //         flash("ok", "Record deleted");

// //         fetchEnrollments();

// //     };

// //     /* ================= AUDIT ================= */

// //     const [auditOpen, setAuditOpen] = useState(false);
// //     const [auditRows, setAuditRows] = useState([]);

// //     const openAuditAll = async () => {

// //         setAuditOpen(true);

// //         const res = await fetch(`${BASE_URL}/api/enrollment-verification/audits`, {
// //             headers: authHeaders
// //         });

// //         setAuditRows(await safeJson(res));

// //     };

// //     const exportExcel = () => {
// //         if (!rows.length) return flash("err", "No data to export");
// //         const ws = XLSX.utils.json_to_sheet(rows);
// //         const wb = XLSX.utils.book_new();
// //         XLSX.utils.book_append_sheet(wb, ws, "Enrollments");
// //         XLSX.writeFile(wb, "enrollment_verification.xlsx");
// //     };

// //     /* ================= TEMPLATE ================= */

// //     const downloadTemplate = () => {
// //         const a = document.createElement("a");
// //         a.href = "/templates/enrollment_verification_upload_template.xlsx";
// //         a.download = "enrollment_verification_upload_template.xlsx";
// //         document.body.appendChild(a);
// //         a.click();
// //         a.remove();
// //     };

// //     /* ================= UI ================= */

// //     return (

// //         <div className="cmp-page">

// //             <div className="header">
// //                 <h1>Enrollment Verification</h1>

// //                 {/* <button className="btn" onClick={openAuditAll}>
// //                     View Audit Logs
// //                 </button> */}

// //             </div>

// //             {msg.text && (
// //                 <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
// //                     <span>{msg.text}</span>
// //                     <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
// //                 </div>
// //             )}

// //             {/* Upload */}

// //             <div className="card grid-3">

// //                 <input
// //                     type="file"
// //                     onChange={e => setFile(e.target.files[0])}
// //                 />

// //                 <button className="btn btn-primary" onClick={uploadExcel}>
// //                     {uploading ? "Uploading..." : "Upload Excel"}
// //                 </button>

// //                 <button className="btn btn-outline" onClick={downloadTemplate}>
// //                     Download Excel Template
// //                 </button>

// //             </div>

// //             {/* Table */}

// //             <div className="card">

// //                 <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
// //                     <input
// //                         className="search"
// //                         placeholder="Search mobile / name / project..."
// //                         value={search}
// //                         onChange={e => setSearch(e.target.value)}
// //                         style={{ flex: "0 0 70%", marginBottom: 0 }}
// //                     />
// //                     <button
// //                         className="btn btn-outline"
// //                         onClick={exportExcel}
// //                         style={{ flex: "0 0 calc(30% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
// //                     >
// //                         Export All Data
// //                     </button>
// //                 </div>

// //                 <div className="table-wrap">

// //                     <table className="ep-table">

// //                         <thead>

// //                             <tr>
// //                                 <th>#</th>
// //                                 {/* <th>ID</th> */}
// //                                 <th>Beneficiary ID</th>
// //                                 <th>Project ID</th>
// //                                 <th>Date</th>
// //                                 <th>Centre</th>
// //                                 <th>Salutation</th>
// //                                 <th>Name</th>
// //                                 <th>Age</th>
// //                                 <th>Gender</th>
// //                                 <th>Mobile</th>
// //                                 <th>WhatsApp No</th>
// //                                 <th>Aadhar No</th>
// //                                 <th>Email</th>
// //                                 <th>Education</th>
// //                                 <th>Guardian</th>
// //                                 <th>Work Experience</th>
// //                                 <th>Willing To Take Job</th>
// //                                 <th>Willing To Migrate</th>
// //                                 <th>Enrolled Last 6 Months</th>
// //                                 <th>Interested Course</th>
// //                                 <th>Heard About Us</th>
// //                                 <th>Counselled</th>
// //                                 <th>ROC Status</th>
// //                                 <th className="actions">Actions</th>
// //                             </tr>

// //                         </thead>

// //                         <tbody>

// //                             {filtered.slice(0, visibleCount).map((r, i) => (

// //                                 <tr key={r.id}>

// //                                     <td>{i + 1}</td>
// //                                     {/* <td>{r.id}</td> */}
// //                                     <td>{r.beneficiaryId}</td>
// //                                     <td>{r.projectId}</td>
// //                                     <td>{r.mobilizationDate}</td>
// //                                     <td>{r.centreName}</td>
// //                                     <td>{r.salutation}</td>
// //                                     <td>{r.name}</td>
// //                                     <td>{r.age}</td>
// //                                     <td>{r.gender}</td>
// //                                     <td>{r.mobileNo}</td>
// //                                     <td>{r.whatsappNumber}</td>
// //                                     <td>{r.aadharNumber}</td>
// //                                     <td>{r.emailId}</td>
// //                                     <td>{r.highestEducation}</td>
// //                                     <td>{r.guardianName}</td>
// //                                     <td>{r.workExperience}</td>
// //                                     <td>{r.willingToTakeJob}</td>
// //                                     <td>{r.willingToMigrate}</td>
// //                                     <td>{r.enrolledLast6Months}</td>
// //                                     <td>{r.interestedCourseName}</td>
// //                                     <td>{r.hearAboutUs}</td>
// //                                     <td>{r.counselled}</td>
// //                                     <td>{r.rocVerificationStatus}</td>

// //                                     <td className="actions">

// //                                         <button
// //                                             className="btn btn-sm btn-danger"
// //                                             onClick={() => deleteEnrollment(r.id)}
// //                                         >
// //                                             Delete
// //                                         </button>

// //                                     </td>

// //                                 </tr>

// //                             ))}

// //                             {!filtered.length && (
// //                                 <tr>
// //                                     <td colSpan="25" className="muted">
// //                                         {loading ? "Loading..." : "No records found"}
// //                                     </td>
// //                                 </tr>
// //                             )}
// //                             {filtered.length > visibleCount && (
// //                                 <tr>
// //                                     <td colSpan="25" style={{ textAlign: "center", padding: "14px" }}>
// //                                         <button
// //                                             className="btn btn-outline"
// //                                             onClick={() => setVisibleCount((c) => c + 500)}
// //                                         >
// //                                             Load More ({filtered.length - visibleCount} remaining)
// //                                         </button>
// //                                     </td>
// //                                 </tr>
// //                             )}

// //                         </tbody>

// //                     </table>

// //                 </div>

// //             </div>

// //             {/* AUDIT MODAL */}

// //             {auditOpen && createPortal(

// //                 <div className="modal-overlay" onClick={() => setAuditOpen(false)}>

// //                     <div className="ep-modal wide" onClick={e => e.stopPropagation()}>

// //                         <h2>Enrollment Verification Audit</h2>

// //                         <pre>{JSON.stringify(auditRows, null, 2)}</pre>

// //                     </div>

// //                 </div>,

// //                 document.body

// //             )}

// //             <style>{`

// // .cmp-page{
// // padding:22px;
// // background:#f7f7fb;
// // min-height:100vh;
// // font-family:Inter
// // }

// // .header{
// // display:flex;
// // justify-content:space-between;
// // align-items:center
// // }

// // h1{
// // font-size:26px;
// // font-weight:900
// // }

// // .flash{
// // padding:10px;
// // border-radius:10px;
// // margin:10px 0;
// // font-weight:700
// // }

// // .flash-ok{
// // background:#ecfdf5;
// // color:#065f46
// // }

// // .flash-err{
// // background:#fef2f2;
// // color:#991b1b
// // }

// // .card{
// // background:#fff;
// // border:1px solid #e5e7eb;
// // border-radius:14px;
// // padding:16px;
// // margin-bottom:14px
// // }

// // .grid-3{
// // display:grid;
// // grid-template-columns:1fr auto auto;
// // gap:10px
// // }

// // .btn{
// // border:1px solid #e5e7eb;
// // padding:10px 14px;
// // border-radius:12px;
// // font-weight:800;
// // cursor:pointer;
// // background:#fff
// // }

// // .btn-primary{
// // background:#2563eb;
// // color:#fff
// // }

// // .btn-sm{
// // padding:6px 10px;
// // font-size:12px
// // }

// // .btn-danger{
// // color:#991b1b;
// // border-color:#fca5a5
// // }

// // .search{
// // margin-bottom:10px;
// // padding:10px;
// // border-radius:12px;
// // border:1px solid #e5e7eb
// // }

// // .table-wrap{
// // overflow-x:auto
// // }

// // .ep-table{
// // width:100%;
// // min-width:1200px;
// // border-collapse:collapse
// // }

// // th,td{
// // padding:10px;
// // border-bottom:1px solid #e5e7eb;
// // font-size:13px;
// // white-space:nowrap
// // }

// // th{
// // background:#fcfcff;
// // text-align:left
// // }

// // td.actions{
// // position:sticky;
// // right:0;
// // background:#fff
// // }

// // .muted{
// // color:#6b7280;
// // font-weight:700
// // }

// // .modal-overlay{
// // position:fixed;
// // inset:0;
// // background:rgba(0,0,0,.45);
// // display:flex;
// // align-items:center;
// // justify-content:center;
// // z-index:99999
// // }

// // .ep-modal{
// // background:#fff;
// // padding:18px;
// // border-radius:16px;
// // width:900px;
// // max-height:90vh;
// // overflow:auto
// // }

// // `}</style>

// //         </div>

// //     );

// // }

// // // import React, { useEffect, useMemo, useState } from "react";
// // // import { createPortal } from "react-dom";

// // // const BASE_URL = "https://mis.empowerplus.in";

// // // export default function EnrollmentVerificationPage() {
// // //     const token = localStorage.getItem("token");

// // //     /* ================= COMMON ================= */
// // //     const [msg, setMsg] = useState({ type: "", text: "" });
// // //     const flash = (type, text) => {
// // //         setMsg({ type, text });
// // //         setTimeout(() => setMsg({ type: "", text: "" }), 3000);
// // //     };

// // //     const authHeaders = useMemo(
// // //         () => ({ Authorization: `Bearer ${token}` }),
// // //         [token]
// // //     );

// // //     const jsonHeaders = useMemo(
// // //         () => ({
// // //             Authorization: `Bearer ${token}`,
// // //             "Content-Type": "application/json",
// // //         }),
// // //         [token]
// // //     );

// // //     const safeJson = async (res) => {
// // //         const t = await res.text();
// // //         try {
// // //             return t ? JSON.parse(t) : [];
// // //         } catch {
// // //             return [];
// // //         }
// // //     };

// // //     /* ================= EXCEL UPLOAD ================= */
// // //     const [file, setFile] = useState(null);
// // //     const [uploading, setUploading] = useState(false);

// // //     const uploadExcel = async () => {
// // //         if (!file) return flash("err", "Select Excel file");

// // //         const fd = new FormData();
// // //         fd.append("file", file);

// // //         setUploading(true);
// // //         try {
// // //             await fetch(
// // //                 `${BASE_URL}/enrollment-verification/upload?actor=Rahul`,
// // //                 {
// // //                     method: "POST",
// // //                     headers: authHeaders,
// // //                     body: fd,
// // //                 }
// // //             );
// // //             flash("ok", "Mobilization Excel uploaded");
// // //             setFile(null);
// // //             fetchEnrollments();
// // //         } catch {
// // //             flash("err", "Upload failed");
// // //         } finally {
// // //             setUploading(false);
// // //         }
// // //     };

// // //     /* ================= TABLE ================= */
// // //     const [rows, setRows] = useState([]);
// // //     const [loading, setLoading] = useState(false);
// // //     const [search, setSearch] = useState("");

// // //     const fetchEnrollments = async () => {
// // //         setLoading(true);
// // //         try {
// // //             const res = await fetch(`${BASE_URL}/api/enrollment-verification`, {
// // //                 headers: authHeaders,
// // //             });

// // //             setRows(await safeJson(res));
// // //         } catch {
// // //             setRows([]);
// // //         } finally {
// // //             setLoading(false);
// // //         }
// // //     };

// // //     useEffect(() => {
// // //         fetchEnrollments();
// // //     }, []);

// // //     const filtered = useMemo(() => {
// // //         const q = search.toLowerCase();

// // //         return rows.filter(
// // //             (r) =>
// // //                 r.mobileNo?.toLowerCase().includes(q) ||
// // //                 r.name?.toLowerCase().includes(q) ||
// // //                 r.projectName?.toLowerCase().includes(q)
// // //         );
// // //     }, [rows, search]);

// // //     /* ================= EDIT ================= */
// // //     const emptyForm = {
// // //         // // beneficiaryId: "",

// // //         // // department: "",
// // //         // dateOfMobilization: "",

// // //         // centreName: "",
// // //         // projectName: "",

// // //         // mobilizationType: "",
// // //         // mobilizerName: "",
// // //         // areaLocation: "",

// // //         // salutation: "",
// // //         // name: "",
// // //         // gender: "",
// // //         // age: "",

// // //         // mobileNo: "",
// // //         // whatsappNumber: "",
// // //         // aadharNumber: "",
// // //         // emailId: "",

// // //         // highestEducationQualification: "",
// // //         // guardianName: "",

// // //         // workExperience: "",
// // //         // willingToTakeJob: "",
// // //         // willingToMigrate: "",

// // //         // enrolledLast6Months: "",
// // //         // interestedCourseName: "",
// // //         // hearAboutUs: "",
// // //         // counselled: "",
// // //         // rocVerificationStatus: ""
// // //     };


// // //     const [editingId, setEditingId] = useState(null);
// // //     const [form, setForm] = useState(emptyForm);
// // //     // const openEdit = (row) => {
// // //     //     setEditingId(row.beneficiaryId);

// // //     //     setForm({
// // //     //         beneficiaryId: row.beneficiaryId ?? "",

// // //     //         // department: row.department ?? "",
// // //     //         dateOfMobilization: row.dateOfMobilization ?? "",

// // //     //         centreName: row.centreName ?? "",
// // //     //         projectName: row.projectName ?? "",

// // //     //         mobilizationType: row.mobilizationType ?? "",
// // //     //         mobilizerName: row.mobilizerName ?? "",
// // //     //         areaLocation: row.areaLocation ?? "",

// // //     //         salutation: row.salutation ?? "",
// // //     //         name: row.name ?? "",
// // //     //         gender: row.gender ?? "",
// // //     //         age: row.age ?? "",

// // //     //         mobileNo: row.mobileNo ?? "",
// // //     //         whatsappNumber: row.whatsappNumber ?? "",
// // //     //         aadharNumber: row.aadharNumber ?? "",
// // //     //         emailId: row.emailId ?? "",

// // //     //         highestEducationQualification: row.highestEducationQualification ?? "",
// // //     //         guardianName: row.guardianName ?? "",

// // //     //         workExperience: row.workExperience ?? "",
// // //     //         willingToTakeJob: row.willingToTakeJob ?? "",
// // //     //         willingToMigrate: row.willingToMigrate ?? "",

// // //     //         enrolledLast6Months: row.enrolledLast6Months ?? "",
// // //     //         interestedCourseName: row.interestedCourseName ?? "",
// // //     //         hearAboutUs: row.hearAboutUs ?? "",
// // //     //         counselled: row.counselled ?? "",
// // //     //         rocVerificationStatus: row.rocVerificationStatus ?? ""
// // //     //     });
// // //     // };

// // //     // const updateMobilization = async () => {
// // //     //     await fetch(
// // //     //         `${BASE_URL}/enrollment-verification?beneficiaryId=${encodeURIComponent(
// // //     //             editingId
// // //     //         )}&actor=Rahul`,
// // //     //         {
// // //     //             method: "PUT",
// // //     //             headers: jsonHeaders,
// // //     //             body: JSON.stringify(form),
// // //     //         }
// // //     //     );
// // //     //     flash("ok", "Mobilization updated");
// // //     //     setEditingId(null);
// // //     //     fetchEnrollments();
// // //     // };

// // //     /* ================= DELETE ================= */
// // //     // const deleteEnrollment = async (id) => {
// // //     //     if (!window.confirm("Deactivate this mobilization?")) return;

// // //     //     await fetch(
// // //     //         `${BASE_URL}/enrollment-verification?beneficiaryId=${encodeURIComponent(
// // //     //             beneficiaryId
// // //     //         )}&actor=Rahul`,
// // //     //         {
// // //     //             method: "DELETE",
// // //     //             headers: authHeaders,
// // //     //         }
// // //     //     );
// // //     //     flash("ok", "Mobilization deactivated");
// // //     //     fetchEnrollments();
// // //     // };

// // //     /* ================= AUDIT ================= */
// // //     const [auditOpen, setAuditOpen] = useState(false);
// // //     const [auditRows, setAuditRows] = useState([]);
// // //     const [auditBeneficiary, setAuditBeneficiary] = useState("");

// // //     const openAuditAll = async () => {
// // //         setAuditOpen(true);
// // //         setAuditBeneficiary("");
// // //         const res = await fetch(
// // //             `${BASE_URL}/mobilization-audits`,
// // //             { headers: authHeaders }
// // //         );
// // //         setAuditRows(await safeJson(res));
// // //     };

// // //     // const openAuditByBeneficiary = async (beneficiaryId) => {
// // //     //     setAuditOpen(true);
// // //     //     setAuditBeneficiary(beneficiaryId);
// // //     //     const res = await fetch(
// // //     //         `${BASE_URL}/mobilization-audits/by-beneficiary?beneficiaryId=${encodeURIComponent(
// // //     //             beneficiaryId
// // //     //         )}`,
// // //     //         { headers: authHeaders }
// // //     //     );
// // //     //     setAuditRows(await safeJson(res));
// // //     // };


// // //     const FormField = ({ label, value, onChange, type = "text" }) => (
// // //         <div className="field">
// // //             <label>{label}</label>
// // //             <input
// // //                 type={type}
// // //                 value={value || ""}
// // //                 onChange={(e) => onChange(e.target.value)}
// // //             />
// // //         </div>
// // //     );

// // //     const downloadTemplate = () => {
// // //         const a = document.createElement("a");
// // //         a.href = "/templates/enrollment_verification_upload_template.xlsx"; // local path
// // //         a.download = "enrollment_verification_upload_template.xlsx";
// // //         document.body.appendChild(a);
// // //         a.click();
// // //         a.remove();
// // //     };


// // //     /* ================= UI ================= */
// // //     return (
// // //         <div className="cmp-page">
// // //             <div className="header">
// // //                 <h1>Enrollment Verification</h1>
// // //                 {/* <button className="btn" onClick={openAuditAll}>
// // //                     View All Audits
// // //                 </button> */}
// // //             </div>

// // //             {msg.text && (
// // //                 <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`}>
// // //                     {msg.text}
// // //                 </div>
// // //             )}

// // //             {/* Upload */}
// // //             <div className="card grid-3">
// // //                 <input type="file" onChange={(e) => setFile(e.target.files[0])} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#fff" }} />
// // //                 <button className="btn btn-primary" onClick={uploadExcel}>
// // //                     {uploading ? "Uploading..." : "Upload Excel"}
// // //                 </button>

// // //                 <button className="btn btn-outline" onClick={downloadTemplate}>
// // //                     Download Excel Template
// // //                 </button>
// // //             </div>

// // //             {/* Table */}
// // //             <div className="card">
// // //                 <input
// // //                     className="search"
// // //                     placeholder="Search beneficiary / name / project..."
// // //                     value={search}
// // //                     onChange={(e) => setSearch(e.target.value)}
// // //                 />

// // //                 <div className="table-wrap">
// // //                     <table className="ep-table">
// // //                         <thead>
// // //                             <tr>
// // //                                 <th>ID</th>
// // //                                 <th>Mobilization Date</th>
// // //                                 <th>Project</th>
// // //                                 <th>Centre</th>
// // //                                 <th>Name</th>
// // //                                 <th>Age</th>
// // //                                 <th>Mobile</th>
// // //                                 <th>Gender</th>
// // //                                 <th>Highest Education</th>
// // //                                 <th>Guardian</th>
// // //                                 <th>ROC Status</th>
// // //                                 <th className="actions">Actions</th>
// // //                             </tr>
// // //                         </thead>

// // //                         <tbody>
// // //                             {filtered.map((r) => (
// // //                                 <tr key={r.id}>
// // //                                     <td>{r.id}</td>
// // //                                     <td>{r.mobilizationDate}</td>
// // //                                     <td>{r.projectName}</td>
// // //                                     <td>{r.centreName}</td>
// // //                                     <td>{r.name}</td>
// // //                                     <td>{r.age}</td>
// // //                                     <td>{r.mobileNo}</td>
// // //                                     <td>{r.gender}</td>
// // //                                     <td>{r.highestEducation}</td>
// // //                                     <td>{r.guardianName}</td>
// // //                                     <td>{r.rocVerificationStatus}</td>

// // //                                     {/* <td className="actions">
// // //                                         <button
// // //                                             className="btn btn-sm btn-danger"
// // //                                             onClick={() => deleteEnrollment(r.id)}
// // //                                         >
// // //                                             Deactivate
// // //                                         </button>
// // //                                     </td> */}
// // //                                 </tr>
// // //                             ))}

// // //                             {!filtered.length && (
// // //                                 <tr>
// // //                                     <td colSpan="12" className="muted">
// // //                                         {loading ? "Loading..." : "No records found"}
// // //                                     </td>
// // //                                 </tr>
// // //                             )}
// // //                         </tbody>

// // //                     </table>
// // //                 </div>
// // //             </div>

// // //             {/* EDIT MODAL */}
// // //             {editingId &&
// // //                 createPortal(
// // //                     <div className="modal-overlay" onClick={() => setEditingId(null)}>
// // //                         <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
// // //                             <h2>Edit Mobilization</h2>

// // //                             <div className="form-grid">

// // //                                 <FormField label="Department" value={form.department}
// // //                                     onChange={(v) => setForm(p => ({ ...p, department: v }))} />

// // //                                 <FormField label="Date Of Mobilization" type="date"
// // //                                     value={form.dateOfMobilization}
// // //                                     onChange={(v) => setForm(p => ({ ...p, dateOfMobilization: v }))} />

// // //                                 <FormField label="Centre Name" value={form.centreName}
// // //                                     onChange={(v) => setForm(p => ({ ...p, centreName: v }))} />

// // //                                 <FormField label="Project Name" value={form.projectName}
// // //                                     onChange={(v) => setForm(p => ({ ...p, projectName: v }))} />

// // //                                 <FormField label="Mobilization Type" value={form.mobilizationType}
// // //                                     onChange={(v) => setForm(p => ({ ...p, mobilizationType: v }))} />

// // //                                 <FormField label="Mobilizer Name" value={form.mobilizerName}
// // //                                     onChange={(v) => setForm(p => ({ ...p, mobilizerName: v }))} />

// // //                                 <FormField label="Name" value={form.name}
// // //                                     onChange={(v) => setForm(p => ({ ...p, name: v }))} />

// // //                                 <FormField label="Gender" value={form.gender}
// // //                                     onChange={(v) => setForm(p => ({ ...p, gender: v }))} />

// // //                                 <FormField label="Age" type="number" value={form.age}
// // //                                     onChange={(v) => setForm(p => ({ ...p, age: v }))} />

// // //                                 <FormField label="Mobile No" value={form.mobileNo}
// // //                                     onChange={(v) => setForm(p => ({ ...p, mobileNo: v }))} />

// // //                                 <FormField label="WhatsApp Number" value={form.whatsappNumber}
// // //                                     onChange={(v) => setForm(p => ({ ...p, whatsappNumber: v }))} />

// // //                                 <FormField label="Email ID" value={form.emailId}
// // //                                     onChange={(v) => setForm(p => ({ ...p, emailId: v }))} />
// // //                                 {/*
// // //                                 <FormField label="ROC Verification Status" value={form.rocVerificationStatus}
// // //                                     onChange={(v) => setForm(p => ({ ...p, rocVerificationStatus: v }))} /> */}

// // //                             </div>

// // //                             <div className="modal-actions">
// // //                                 <button className="btn" onClick={() => setEditingId(null)}>Cancel</button>
// // //                                 {/* <button className="btn btn-primary" onClick={updateMobilization}>Save</button> */}
// // //                             </div>
// // //                         </div>
// // //                     </div>,
// // //                     document.body
// // //                 )
// // //             }


// // //             {/* AUDIT MODAL */}
// // //             {auditOpen &&
// // //                 createPortal(
// // //                     <div className="modal-overlay" onClick={() => setAuditOpen(false)}>
// // //                         <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
// // //                             <h2>Mobilization Audit Logs</h2>
// // //                             {auditBeneficiary && (
// // //                                 <div className="muted">
// // //                                     Beneficiary: <b>{auditBeneficiary}</b>
// // //                                 </div>
// // //                             )}
// // //                             <pre>{JSON.stringify(auditRows, null, 2)}</pre>
// // //                         </div>
// // //                     </div>,
// // //                     document.body
// // //                 )}

// // //             {/* INLINE CSS */}
// // //             <style>{`
// // //         .input-file {
// // //                  border: "1px solid #e5e7eb",
// // //         borderRadius: 10,
// // //         padding: "10px 12px",
// // //         background: "#fff",}
// // //             .table-wrap {
// // //   width: 100%;
// // //   overflow-x: auto;
// // //   -webkit-overflow-scrolling: touch;
// // // }

// // // .ep-table {
// // //   width: max-content;      /* KEY */
// // //   min-width: 2600px;       /* enough for all columns */
// // //   border-collapse: collapse;
// // // }

// // // .ep-table th,
// // // .ep-table td {
// // //   padding: 10px 12px;
// // //   border-bottom: 1px solid #e5e7eb;
// // //   font-size: 13px;
// // //   white-space: nowrap;     /* PREVENT WRAP */
// // // }

// // // .ep-table th {
// // //   background: #fcfcff;
// // //   position: sticky;
// // //   top: 0;
// // //   z-index: 2;
// // // }

// // // /* Sticky Actions Column */
// // // .ep-table td.actions,
// // // .ep-table th.actions {
// // //   position: sticky;
// // //   right: 0;
// // //   background: #fff;
// // //   box-shadow: -6px 0 8px rgba(0,0,0,0.05);
// // //   z-index: 3;
// // // }



// // //       .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter }
// // //       .header { display:flex; justify-content:space-between; align-items:center }
// // //       h1 { font-size:26px; font-weight:900 }

// // //       .flash { padding:10px; border-radius:10px; margin:10px 0; font-weight:700 }
// // //       .flash-ok { background:#ecfdf5; color:#065f46 }
// // //       .flash-err { background:#fef2f2; color:#991b1b }

// // //       .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; margin-bottom:14px }
// // //       .grid-3 { display:grid; grid-template-columns:1fr auto auto; gap:10px }

// // //       .btn { border:1px solid #e5e7eb; padding:10px 14px; border-radius:12px; font-weight:800; cursor:pointer; background:#fff }
// // //       .btn-primary { background:#2563eb; color:#fff }
// // //       .btn-sm { padding:6px 10px; font-size:12px }
// // //       .btn-danger { color:#991b1b; border-color:#fca5a5 }

// // //       .search { margin-bottom:10px; padding:10px; border-radius:12px; border:1px solid #e5e7eb }

// // //       .table-wrap { overflow-x:auto }
// // //       .ep-table { width:100%; min-width:1200px; border-collapse:collapse }
// // //       th, td { padding:10px; border-bottom:1px solid #e5e7eb; font-size:13px; white-space:nowrap }
// // //       th { background:#fcfcff; text-align:left }
// // //       td.actions { position:sticky; right:0; background:#fff }

// // //       .muted { color:#6b7280; font-weight:700 }

// // //       .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:99999 }
// // //       .ep-modal { background:#fff; padding:18px; border-radius:16px; width:520px; max-height:90vh; overflow:auto }
// // //       .ep-modal.wide { width:1000px }

// // //       .form-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:12px }
// // //       .field { display:flex; flex-direction:column; gap:6px }
// // //       .field label { font-size:11px; font-weight:800; color:#6b7280 }
// // //       .field input { padding:10px; border-radius:10px; border:1px solid #e5e7eb }

// // //       .modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:10px }
// // //       `}</style>
// // //         </div>
// // //     );
// // // }
